import type { QueryClient } from "@tanstack/react-query";
import {
  createPrincipalWalletSet,
  type PrincipalWalletSet,
} from "~/lib/auth/principal-wallets";
import type { WalletUserLike } from "~/lib/auth/wallet-resolution";
import type { VmPreference } from "~/lib/auth/vm-types";
import {
  createAuthQueryScope,
  createSessionQueryScope,
  type AuthQueryScope,
  type SessionQueryScope,
} from "~/lib/query-keys";
import { getBrowserPlatformApiPreloadClient } from "~/lib/trpc/browser-client";
import type { PlatformApiTrpcClient } from "~/lib/trpc/platform-api-adapter";

export interface RouterAuthRuntime {
  authQueryScope: AuthQueryScope;
  authenticated: boolean;
  principalWallets: PrincipalWalletSet;
  primaryWalletAddress: string | null;
  ready: boolean;
  sessionQueryScope: SessionQueryScope;
  user: WalletUserLike;
  vmPreference: VmPreference;
  vmPreferenceLoaded: boolean;
}

export interface AppRouterContext {
  auth: RouterAuthRuntime;
  platformApi: PlatformApiTrpcClient;
  queryClient: QueryClient;
}

interface CreateRouterRuntimeContextInput {
  authenticated?: boolean;
  platformApi?: PlatformApiTrpcClient;
  queryClient: QueryClient;
  ready?: boolean;
  user?: WalletUserLike;
  vmPreference?: VmPreference;
  vmPreferenceLoaded?: boolean;
}

function resolvePrimaryWalletAddress(
  principalWallets: PrincipalWalletSet,
): string | null {
  if (principalWallets.primaryWallet?.address) {
    return principalWallets.primaryWallet.address;
  }

  return principalWallets.ownerAddresses[0] ?? null;
}

export function createRouterRuntimeContext(
  input: CreateRouterRuntimeContextInput,
): AppRouterContext {
  const authenticated = input.authenticated ?? false;
  const ready = input.ready ?? false;
  const user = input.user ?? null;
  const vmPreference = input.vmPreference ?? "auto";
  const vmPreferenceLoaded = input.vmPreferenceLoaded ?? false;
  const principalWallets = createPrincipalWalletSet({
    user,
    vmPreference,
  });
  const primaryWalletAddress = resolvePrimaryWalletAddress(principalWallets);
  const authQueryScope = createAuthQueryScope({
    authenticated,
    user,
    walletAddress: primaryWalletAddress,
  });
  const sessionQueryScope = createSessionQueryScope({
    authenticated,
    user,
    vmPreference,
    walletAddress: primaryWalletAddress,
  });

  return {
    auth: {
      authQueryScope,
      authenticated,
      principalWallets,
      primaryWalletAddress,
      ready,
      sessionQueryScope,
      user,
      vmPreference,
      vmPreferenceLoaded,
    },
    platformApi: input.platformApi ?? getBrowserPlatformApiPreloadClient(),
    queryClient: input.queryClient,
  };
}
